/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useRef, useEffect, useState } from 'react';

// Mantine
import { Drawer, Box, ScrollArea } from '@mantine/core';
import { useForm, isNotEmpty } from '@mantine/form';

// Styles
import classes from './AddMeterPoints.module.css';

// Components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';

// Components : Steps
import { AddMeterPointsForm } from './AddMeterPointsForm';

// Store
import useStore from '@store/index';
import { useTranslation } from 'react-i18next';
import { randomId } from '@mantine/hooks';
import { useLocation } from 'react-router';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddMeterPoints = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** STATES ******************************* */
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);
	/* ******************************** HOOKS *********************************** */
	const viewport = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();
	const location = useLocation();

	const {
		offsetCreationActiveStep,
		setOffsetCreationActiveStep,
		setParamsFromProject,
		paramsFromProject,
	} = useStore();

	// Validators
	const form = useForm({
		initialValues: {
			project: '',
			site: '',
			entity: '',
			meterPoints: [
				{
					id: randomId(),
					location: {
						countryCode: '',
					},
					serialNumber: '',
				},
			],
		},

		validate: {
			entity: isNotEmpty(t('global.entityRequired')),
			site: isNotEmpty(t('global.siteRequired')),
			project: isNotEmpty(t('global.projectRequired')),
			meterPoints: {
				serialNumber: isNotEmpty(
					t('global.serialNumberRequired')
				),
				location: {
					countryCode: isNotEmpty(
						t('global.countryRequired')
					),
				},
			},
		},
	});

	useEffect(() => {
		if (
			offsetCreationActiveStep >= 0 &&
			viewport &&
			viewport.current
		) {
			viewport.current!.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [offsetCreationActiveStep]);

	useEffect(() => {
		setOffsetCreationActiveStep(0);
	}, []);

	useEffect(() => {
		if (
			paramsFromProject &&
			paramsFromProject.producer &&
			paramsFromProject.project &&
			paramsFromProject.site
		) {
			form.setFieldValue(
				'entity',
				paramsFromProject.producer
			);
			form.setFieldValue(
				'project',
				paramsFromProject.project
			);
			form.setFieldValue('site', paramsFromProject.site);
		}
	}, [paramsFromProject]);

	const isPreviousMeasurabilityProjectDetails =
		location.pathname.split('/')[1] ===
		'measurability-projects';

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={() => {
						props.onClose();
						setIsOpenModalCancel(false);
						setParamsFromProject({});
						form.reset();
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={
						isPreviousMeasurabilityProjectDetails
							? t('meterPoints.backToProjectDetailsSure')
							: t('meterPoints.backToAllMeterPointSure')
					}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{isPreviousMeasurabilityProjectDetails
							? t('meterPoints.backToProjectDetails')
							: t('meterPoints.backToAllMeterPoints')}
					</PreviousDrawerButton>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						<ScrollArea
							viewportRef={viewport}
							className={classes.containerContent}
						>
							<AddMeterPointsForm
								isPreviousMeasurabilityProjectDetails={
									isPreviousMeasurabilityProjectDetails
								}
								form={form}
								onCancel={() => {
									setIsOpenModalCancel(true);
								}}
								onBack={() => {
									props.onClose();
									form.reset();
								}}
							/>
						</ScrollArea>
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />

					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
